import routes from "routes/routes";
import { ORGANIZATION_ID_PARAM } from "./organizationAdmin";
import { SeriesRequestBody, SeriesSeasonInfo } from "api/account/types";

/**
 * The organisation series screen's pure parts (series booking, step 11).
 *
 * The client never computes dates or conflicts (ADR-0003 amendment): it sends
 * the pattern and shows what the server's preview returns. What lives here is
 * only the translation of the form into that pattern, and of the server's
 * refusals into messages.
 */

/** Same URL-restore rule as the plan: the organization lives in the URL. */
export const makeOrganizationSeriesPath = (organizationId: string): string =>
  routes.accountOrganizationSeriesPath.replace(
    `:${ORGANIZATION_ID_PARAM}`,
    encodeURIComponent(organizationId)
  );

/** Monday first, as the week reads here; values are the server's (0 = Sunday). */
export const WEEKDAYS: ReadonlyArray<{ value: number; key: string }> = [
  { value: 1, key: "mon" },
  { value: 2, key: "tue" },
  { value: 3, key: "wed" },
  { value: 4, key: "thu" },
  { value: 5, key: "fri" },
  { value: 6, key: "sat" },
  { value: 0, key: "sun" },
];

export interface SeriesForm {
  groupId: string;
  weekdays: number[];
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  numberOfGuests: string;
  comment: string;
}

/** Why the form cannot be sent yet, or null. Checked before any request. */
export const seriesFormProblem = (form: SeriesForm): string | null => {
  if (!form.groupId) return "room_missing";
  if (form.weekdays.length === 0) return "weekdays_missing";
  if (!form.startTime || !form.endTime) return "time_missing";
  if (form.endTime <= form.startTime) return "time_order";
  if (!form.startDate || !form.endDate) return "dates_missing";
  if (form.endDate < form.startDate) return "date_order";
  return null;
};

/** The request body the app route forwards to the series module. */
export const seriesRequestBody = (form: SeriesForm, excludedDates: string[] = []): SeriesRequestBody => {
  const guests = Number.parseInt(form.numberOfGuests, 10);
  return {
    pattern: {
      type: "weekly",
      interval: 1,
      weekdays: [...form.weekdays].sort((a, b) => a - b),
      startDate: form.startDate,
      endCondition: { untilDate: form.endDate },
    },
    startTime: form.startTime,
    endTime: form.endTime,
    groupId: form.groupId,
    ...(Number.isInteger(guests) && guests > 0 ? { numberOfGuests: guests } : {}),
    ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
    excludedDates,
  };
};

/**
 * The first suggestion for the date range: the rest of the season. A season
 * that has already ended gives nothing to suggest — the screen says why.
 */
export const defaultRange = (
  season: SeriesSeasonInfo | null,
  today: string
): { startDate: string; endDate: string } => {
  if (!season || season.endDate < today) return { startDate: "", endDate: "" };
  return { startDate: season.startDate > today ? season.startDate : today, endDate: season.endDate };
};

/**
 * The i18n key (under `account.organization_series.error`) for a refusal.
 * The module's refusals carry a code at the start of the message; anything
 * else is told apart by status, so a failure never reads as "no dates".
 */
export const seriesErrorKey = (error: unknown): string => {
  const e = error as { status?: number | string; data?: { message?: string } } | null;
  const message = typeof e?.data?.message === "string" ? e.data.message : "";
  const code = /^([a-z_]+):/.exec(message)?.[1];
  if (code && ["season_not_configured", "outside_season", "pattern_not_allowed", "not_offered", "too_many_occurrences"].includes(code)) {
    return code;
  }
  switch (e?.status) {
    case 404:
      return "not_allowed";
    case 409:
      return "not_covered";
    case 504:
      return "timeout";
    case 502:
    case 503:
      return "unavailable";
    default:
      return "generic";
  }
};
